"use client";

import { Toast as ToastPrimitive } from "@base-ui/react/toast";
import { Cross2Icon } from "@radix-ui/react-icons";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@/utils/lib";
import { resolveClassName } from "@/utils/resolve-class-name";

const ToastProvider = ToastPrimitive.Provider;
const ToastPortal = ToastPrimitive.Portal;

const ToastViewport = ({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Viewport>) => (
  <ToastPrimitive.Viewport
    className={(state) =>
      cn(
        `
          pointer-events-none fixed bottom-4 left-1/2 z-200
          h-(--toast-frontmost-height) max-h-safe-screen
          w-[calc(100vw-2rem)] max-w-[550px] -translate-x-1/2
          overflow-visible sm:bottom-6
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
ToastViewport.displayName = "ToastViewport";

const toastVariants = cva(
  `
    group pointer-events-auto absolute bottom-0 left-1/2
    flex min-h-fit w-max max-w-full transform-[translateX(-50%)_translateX(var(--toast-swipe-movement-x))_translateY(var(--toast-swipe-movement-y))_translateY(var(--toast-stack-offset))_translateY(var(--toast-index-offset))_translateY(var(--toast-animate-y))]
    items-start justify-between overflow-hidden rounded-xl border p-4
    pr-8
    shadow-lg backdrop-blur-xl
    transition-[opacity,transform,height]
    duration-200
    ease-out
    [--toast-animate-y:0px] [--toast-index-offset:calc(var(--toast-index)*-0.75rem)] [--toast-stack-offset:calc(var(--toast-offset-y)*-1)]
    data-ending-style:opacity-0
    data-ending-style:[--toast-animate-y:1.5rem]
    data-limited:opacity-0
    data-limited:[--toast-animate-y:-1rem]
    data-starting-style:opacity-0
    data-starting-style:[--toast-animate-y:1.5rem]
    data-swiping:transition-none
  `,
  {
    variants: {
      variant: {
        default:
          "border-border/70 bg-background/90 text-foreground shadow-black/20",
        success:
          "border-green-500/40 bg-green-950/90 text-green-50 shadow-black/30",
        notification:
          "border-orange-300/40 bg-orange-950/90 text-orange-50 shadow-black/30",
        destructive:
          "border-red-500/40 bg-red-950/90 text-red-50 shadow-black/30",
        warning:
          "border-yellow-400/40 bg-yellow-950/90 text-yellow-50 shadow-black/30",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

type ToastVariant = NonNullable<VariantProps<typeof toastVariants>["variant"]>;

const Toast = ({
  toast,
  className,
  children,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Root>) => {
  const variant = toast.type as ToastVariant | undefined;

  return (
    <ToastPrimitive.Root
      toast={toast}
      className={(state) =>
        cn(toastVariants({ variant }), resolveClassName(className, state))
      }
      {...props}
    >
      {children}
    </ToastPrimitive.Root>
  );
};
Toast.displayName = "Toast";

const ToastContent = ({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Content>) => (
  <ToastPrimitive.Content
    className={(state) =>
      cn(
        `
          grid w-full gap-1 overflow-visible transition-opacity duration-200
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
ToastContent.displayName = "ToastContent";

const ToastAction = ({
  className,
  nativeButton = false,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Action>) => (
  <ToastPrimitive.Action
    nativeButton={nativeButton}
    className={(state) =>
      cn(
        `
          inline-flex
          h-8
          shrink-0
          items-center
          justify-center
          rounded-md
          border bg-transparent px-3 text-sm font-medium transition-colors group-data-[type=destructive]:border-muted/40
          hover:bg-secondary group-data-[type=destructive]:hover:border-destructive/30 group-data-[type=destructive]:hover:bg-destructive group-data-[type=destructive]:hover:text-white focus:ring-1
          focus:ring-ring
          focus:outline-hidden group-data-[type=destructive]:focus:ring-destructive
          disabled:pointer-events-none disabled:opacity-50
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
ToastAction.displayName = "ToastAction";

const ToastClose = ({
  className,
  nativeButton = false,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Close>) => (
  <ToastPrimitive.Close
    nativeButton={nativeButton}
    className={(state) =>
      cn(
        `
          absolute top-2 right-2 rounded-md p-1 text-muted-foreground
          opacity-100
          transition-colors
          group-data-[type=destructive]:text-red-100/80
          group-data-[type=notification]:text-white/85 group-data-[type=success]:text-white/85 group-data-[type=warning]:text-primary-foreground/85 hover:text-foreground group-data-[type=destructive]:hover:text-white
          group-data-[type=notification]:hover:text-white
          group-data-[type=success]:hover:text-white
          group-data-[type=warning]:hover:text-primary-foreground
          focus:opacity-100
          focus:ring-1
          focus:ring-ring
          focus:outline-hidden
          group-data-[type=destructive]:focus:ring-red-200/50
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  >
    <Cross2Icon className="size-4" />
  </ToastPrimitive.Close>
);
ToastClose.displayName = "ToastClose";

const ToastTitle = ({
  className,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Title>) => (
  <ToastPrimitive.Title
    className={(state) =>
      cn(
        `
          text-sm font-semibold
          group-data-[type=destructive]:text-red-50
          [&+div]:text-xs
        `,
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
ToastTitle.displayName = "ToastTitle";

const ToastDescription = ({
  className,
  render = <div />,
  ...props
}: React.ComponentProps<typeof ToastPrimitive.Description>) => (
  <ToastPrimitive.Description
    render={render}
    className={(state) =>
      cn(
        "min-w-0 text-sm wrap-break-word opacity-90 group-data-[type=destructive]:text-red-100/85",
        resolveClassName(className, state),
      )
    }
    {...props}
  />
);
ToastDescription.displayName = "ToastDescription";

type ToastActionElement = React.ReactElement<typeof ToastAction>;

type ToastProps = {
  title?: React.ReactNode;
  description?: React.ReactNode;
  details?: React.ReactNode;
  duration?: number;
  variant?: ToastVariant;
  action?: ToastActionElement;
  icon?: React.ReactNode;
};

export {
  Toast,
  ToastAction,
  type ToastActionElement,
  ToastClose,
  ToastContent,
  ToastDescription,
  ToastPortal,
  type ToastProps,
  ToastProvider,
  ToastTitle,
  type ToastVariant,
  ToastViewport,
};
